import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { format, parse, isValid } from "date-fns";
import { ChevronLeft } from "lucide-react";
import { useGetAPurchaseReturnEntry } from "../service/purchaseEntryReturnService";
import Loading from "../components/common/LoaderCommon";

const formatPurchaseDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return "-";
  const standardDate = new Date(dateStr);
  if (isValid(standardDate)) {
    return format(standardDate, "dd-MM-yyyy hh:mm a");
  }
  const parsedDate = parse(dateStr, "dd-MM-yyyy", new Date());
  if (isValid(parsedDate)) {
    return format(parsedDate, "dd-MM-yyyy");
  }
  return dateStr;
};

export default function PurchaseDetail() {
  const { id } = useParams<{ id: string }>(); // This will be the purchaseNumber
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vendorId = Number(searchParams.get("vendorId"));

  const { data, isLoading } = useGetAPurchaseReturnEntry({
    purchaseNumber: id || "",
    vendorId,
  });

  if (isLoading) {
    return <Loading />;
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h2 className="text-xl font-semibold text-gray-700">
          {vendorId > 0 ? "Record not found" : "Open this invoice from the purchase list to select its vendor."}
        </h2>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-[#1A5EF9] text-white rounded-[12px] hover:bg-[#0E4FDE] transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  const vendorName = data.vendorName;
  const documentNumber = data.purchaseNumber;
  const documentDate = data.purchaseDate;
  const products = data.purchaseEntryProductResponse || [];
  
  const totalTax = data.totalTax || 0;
  const totalDiscount = data.totalDiscount || 0;
  const grandTotal = data.totalAmount || 0;
  const amountPaid = data.amountPaid || 0;
  const balanceDue = data.balanceAmount || 0;
  
  // A simple subtotal estimate
  const subtotal = products.reduce((acc, curr) => acc + (curr.unitPrice * curr.quantity), 0);

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-10">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-white/50 transition-colors bg-white shadow-sm"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-[#1A5EF9] capitalize flex items-center gap-3">
            Purchase Invoice Detail
            <span className="px-3 py-1 text-xs rounded-full bg-[#E8EEF9] text-[#1A5EF9] font-semibold border border-[#D7E2F5]">
              COMPLETED
            </span>
          </h1>
          <p className="text-sm text-[#7D8998] mt-1">
            {documentNumber} • {formatPurchaseDate(documentDate)}
          </p>
        </div>
      </div>

      {/* Vendor Info Card */}
      <div className="bg-white rounded-[24px] p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)] border border-[#E8EEF9]">
        <h2 className="text-lg font-semibold text-[#21314A] mb-5">
          Vendor Information
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-xs font-medium text-[#8A9BB5] uppercase tracking-wider mb-1">
              Vendor Name
            </p>
            <p className="text-[#21314A] font-medium">{vendorName || "-"}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-[#8A9BB5] uppercase tracking-wider mb-1">
              VATIN
            </p>
            <p className="text-[#21314A] font-medium">{data.vendorVatin || "-"}</p>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-[24px] shadow-[0_16px_40px_rgba(26,94,249,0.08)] border border-[#E8EEF9] overflow-hidden">
        <div className="px-6 py-5 border-b border-[#E8EEF9]">
          <h2 className="text-lg font-semibold text-[#21314A]">Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-[#F8FAFC] text-[#52627C]">
              <tr>
                <th className="px-6 py-4 font-medium">Product</th>
                <th className="px-6 py-4 font-medium text-right">Unit Price</th>
                <th className="px-6 py-4 font-medium text-right">Quantity</th>
                <th className="px-6 py-4 font-medium text-right">Tax (%)</th>
                <th className="px-6 py-4 font-medium text-right">Discount</th>
                <th className="px-6 py-4 font-medium text-right">Total Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8EEF9]">
              {products.map((item: any) => (
                <tr
                  key={item.id}
                  className="hover:bg-[#F8FAFC] transition-colors"
                >
                  <td className="px-6 py-4 text-[#21314A] font-medium">
                    {item.productName || "-"}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {Number(item.unitPrice || 0).toFixed(3)}
                  </td>
                  <td className="px-6 py-4 text-right">{item.quantity}</td>
                  <td className="px-6 py-4 text-right">
                    {item.tax ? Number(item.tax).toFixed(2) : "0.00"}%
                  </td>
                  <td className="px-6 py-4 text-right">
                    {Number(item.discount || 0).toFixed(3)}
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-[#1A5EF9]">
                    {Number(item.totalAmount || 0).toFixed(3)}
                  </td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-[#8A9BB5]"
                  >
                    No items found for this invoice.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="flex flex-col md:flex-row justify-end gap-6">
        <div className="bg-white rounded-[24px] p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)] border border-[#E8EEF9] min-w-[320px]">
          <h3 className="text-base font-semibold text-[#21314A] mb-5">
            Summary
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center text-sm">
              <span className="text-[#52627C]">Subtotal</span>
              <span className="font-medium text-[#21314A]">
                {subtotal.toFixed(3)}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-[#52627C]">Total Discount</span>
              <span className="font-medium text-[#D14343]">
                -{Number(totalDiscount).toFixed(3)}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm pb-4 border-b border-[#E8EEF9]">
              <span className="text-[#52627C]">Total Tax</span>
              <span className="font-medium text-[#21314A]">
                +{Number(totalTax).toFixed(3)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-base font-semibold text-[#21314A]">
                Grand Total
              </span>
              <span className="text-xl font-bold text-[#1A5EF9]">
                {Number(grandTotal).toFixed(3)}
              </span>
            </div>

            <div className="pt-4 space-y-3 border-t border-[#E8EEF9]">
              <div className="flex justify-between items-center text-sm">
                <span className="text-[#52627C]">Amount Paid</span>
                <span className="font-medium text-[#0E9F6E]">
                  {Number(amountPaid).toFixed(3)}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-[#52627C]">Balance Due</span>
                <span className="font-medium text-[#E08A1E]">
                  {Number(balanceDue).toFixed(3)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
